import { useEffect, type ReactNode } from "react";
import { Anchor, Badge, Box, Button, Card, Grid, Group, SimpleGrid, Stack, Table, Tabs, Text, Timeline, Title } from "@mantine/core";
import { IconAdjustmentsHorizontal, IconArrowRight, IconCode, IconCpu } from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
import { DATA_SOURCES, RESPONSE_HEADER_DOCS, REQUEST_HEADER_NAMES, SOURCE_FILTERS, TYPE_FILTERS, type PassioCallRecord } from "@passio/api-client";
import { PageHeader } from "../theme/components";
import { CodeBlock } from "../components/JsonView";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RequestIdChip } from "../components/RequestId";
import { AccountCallout } from "../components/AccountCta";
import { useSession } from "../lib/session";
import { isChainCall, openInspector, useCallHistory } from "../lib/calls";
import { openClientSettings, useClientHeaders } from "../lib/clientSettings";
import { publicApiBase, publicApiOrigin } from "../lib/clients";
import type { CodeLanguage } from "../components/highlight";

const SECTIONS = [
  { id: "anatomy", label: "Anatomy of a call" },
  { id: "request-headers", label: "Request headers" },
  { id: "data", label: "Data sources & filters" },
  { id: "response-headers", label: "Response headers" },
  { id: "feedback", label: "Request IDs & feedback" },
  { id: "models", label: "Models" },
  { id: "errors", label: "Errors & retries" },
];

function Section({ id, title, lead, children }: { id: string; title: string; lead?: ReactNode; children: ReactNode }) {
  return (
    <Box id={id} style={{ scrollMarginTop: 84 }}>
      <Box className="pd-rule" pt="md" mb="md">
        <Title order={2} fz={24}>
          {title}
        </Title>
        {lead && (
          <Text c="dimmed" fz="sm" mt={4} maw={820}>
            {lead}
          </Text>
        )}
      </Box>
      {children}
    </Box>
  );
}

function CodeTabs({ samples }: { samples: { value: string; label: string; language: CodeLanguage; code: string }[] }) {
  return (
    <Tabs defaultValue={samples[0]!.value} variant="pills" radius="xl">
      <Tabs.List mb="xs">
        {samples.map((s) => (
          <Tabs.Tab key={s.value} value={s.value}>
            {s.label}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {samples.map((s) => (
        <Tabs.Panel key={s.value} value={s.value}>
          <CodeBlock code={s.code} language={s.language} maxHeight={460} />
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}

const REQUEST_HEADERS = [
  { name: "Authorization", what: "Bearer token from your license key (the SDK's LicenseKeyAuth exchanges and refreshes it).", example: "Bearer eyJhbGciOi…", required: true },
  { name: "Passio-ID", what: "Your customer id, returned with the token.", example: "6f2c…", required: true },
  { name: REQUEST_HEADER_NAMES.localizationIso, what: "Translates response text (food names, advice) into this language. ISO 639-1.", example: "es" },
  { name: REQUEST_HEADER_NAMES.productLocalization, what: "Picks the regional food database and gives AI routes regional context: local portion names, brands and dishes. BCP 47.", example: "en-GB" },
  { name: REQUEST_HEADER_NAMES.searchConfig, what: "JSON: which data sources to search and how many results from each, plus region, type, source and tag filters. See Data sources & filters.", example: '{"dataSources":[…],"filters":{…}}' },
  { name: REQUEST_HEADER_NAMES.productImages, what: "true adds product and brand image URLs to food results.", example: "true" },
];

const FEEDBACK_BY_ROUTE = [
  { from: "Photo, text and voice extraction", did: "Logged it as returned", send: "accepted" },
  { from: "", did: "Changed, removed or added an item", send: "edited + itemRef (the item) + note (what it became)" },
  { from: "", did: "Discarded the whole result", send: "rejected" },
  { from: "Semantic / advanced search", did: "Picked a result", send: "click (or logged) + itemRef = its refCode" },
  { from: "Suggestions (look-alikes, hidden, possible, predicted)", did: "Kept or dismissed one", send: "accepted / rejected + itemRef = the suggestion" },
  { from: "Meal plans", did: "Kept or discarded the plan", send: "saved / rejected" },
  { from: "Ratings and advisor", did: "Agreed / disagreed", send: "thumbs_up / thumbs_down" },
];

const ERRORS = [
  { code: "400", meaning: "The body or a parameter is invalid; the message names the field.", action: "Fix the input. Don't retry as-is." },
  { code: "401", meaning: "Token missing or expired.", action: "Get a fresh token and retry once. The SDK refreshes before expiry." },
  { code: "403", meaning: "The route isn't in your plan, or the account can't use it (for example a non-ZDR model on a ZDR account).", action: "Don't retry. Ask Passio to enable it, or pick an eligible model." },
  { code: "404", meaning: "Nothing found, for example an unknown barcode or refCode.", action: "Normal outcome: show a not-found state or fall back to search." },
  { code: "429", meaning: "Rate limit or period budget reached.", action: "Wait for Retry-After seconds. X-Period-Usage and X-Budget-Cap show where you stand." },
  { code: "5xx / timeout", meaning: "An upstream or model provider problem. Passio already falls back across models before giving up.", action: "Retry with backoff. Allow up to about 60 s for large photos on AI routes." },
];

function feedbackSamples(base: string) {
  const ts = `// 1. Scan, and keep the request id next to the result
const scan = await passio.vision.extractIngredientsAutoTyped({ image });
const draft = { foods: scan.data, requestId: scan.meta.requestId! };

// 2. When the user saves their log, report what they did with it
const changes = compare(draft.foods, savedFoods); // your own diff
await passio.feedback.send(
  changes.length
    ? changes.map((c) => ({
        requestId: draft.requestId,
        label: "edited",
        itemRef: c.before, // "white rice"
        note: \`changed to \${c.after}\`, // "changed to brown rice"
        clientEventId: \`\${logId}-\${c.before}\`, // safe to retry
      }))
    : { requestId: draft.requestId, label: "accepted", clientEventId: logId },
);

// ...or if they delete the scan instead
await passio.feedback.send({ requestId: draft.requestId, label: "rejected" });

// 3. Check what Passio recorded for the call
const { data: trace } = await passio.feedback.lookup(draft.requestId);`;
  const swift = `// Read the id from the scan response
let (data, response) = try await URLSession.shared.data(for: scanRequest)
let requestId = (response as? HTTPURLResponse)?.value(forHTTPHeaderField: "X-Request-ID")

// Later, when the user logs the meal unchanged
var req = URLRequest(url: URL(string: "${base}/napi/feedback")!)
req.httpMethod = "POST"
req.setValue("Bearer \\(token)", forHTTPHeaderField: "Authorization")
req.setValue(customerId, forHTTPHeaderField: "Passio-ID")
req.setValue("application/json", forHTTPHeaderField: "Content-Type")
req.httpBody = try JSONSerialization.data(withJSONObject: [
  "items": [["requestId": requestId!, "label": "accepted", "clientEventId": logId]]
])
_ = try await URLSession.shared.data(for: req)`;
  const kotlin = `// Read the id from the scan response
val requestId = scanResponse.header("X-Request-ID")

// Later, when the user corrects an item before logging
val body = """{"items":[{"requestId":"$requestId","label":"edited","itemRef":"white rice","note":"changed to brown rice"}]}"""
val req = Request.Builder()
    .url("${base}/napi/feedback")
    .header("Authorization", "Bearer $token")
    .header("Passio-ID", customerId)
    .post(body.toRequestBody("application/json".toMediaType()))
    .build()
client.newCall(req).execute().close()`;
  const curl = `curl -X POST "${base}/napi/feedback" \\
  -H "Authorization: Bearer $PASSIO_TOKEN" \\
  -H "Passio-ID: $PASSIO_CUSTOMER_ID" \\
  -H "Content-Type: application/json" \\
  -d '{"items": [{"requestId": "0192f3a4-5b6c-7d8e-9f00-112233445566", "label": "edited",
       "itemRef": "white rice", "note": "changed to brown rice", "clientEventId": "log-8812"}]}'

# What Passio recorded for that call (route, model, tokens, cost) and the feedback it got
curl "${base}/napi/feedback/0192f3a4-5b6c-7d8e-9f00-112233445566" \\
  -H "Authorization: Bearer $PASSIO_TOKEN" -H "Passio-ID: $PASSIO_CUSTOMER_ID"`;
  const py = `import os, requests

H = {"Authorization": f"Bearer {os.environ['PASSIO_TOKEN']}", "Passio-ID": os.environ["PASSIO_CUSTOMER_ID"]}

scan = requests.post("${base}/napi/tools/vision/extractIngredientsAutoTyped", headers=H, json={"image": image_data_uri})
request_id = scan.headers["X-Request-ID"]  # store it with the draft log

# when the user logs it unchanged
requests.post("${base}/napi/feedback", headers=H, json={"items": [
    {"requestId": request_id, "label": "accepted", "clientEventId": log_id},
]})`;
  return [
    { value: "ts", label: "TypeScript SDK", language: "ts" as const, code: ts },
    { value: "swift", label: "Swift", language: "swift" as const, code: swift },
    { value: "kotlin", label: "Kotlin", language: "kotlin" as const, code: kotlin },
    { value: "curl", label: "curl", language: "shell" as const, code: curl },
    { value: "py", label: "Python", language: "python" as const, code: py },
  ];
}

function RecentRequests() {
  const calls = useCallHistory().filter((c): c is PassioCallRecord => !isChainCall(c) && !!c.meta?.requestId && c.ok && !c.path.includes("/feedback")).slice(0, 6);
  if (!calls.length) {
    return (
      <Text fz="sm" c="dimmed">
        Run anything on another page (a photo, a search, some text) and its request id shows up here, ready to rate.
      </Text>
    );
  }
  return (
    <Stack gap={8}>
      {calls.map((c) => (
        <Group key={c.id} justify="space-between" wrap="wrap" gap="xs" p="xs" style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: 10 }}>
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <Badge size="sm" variant="light" color="gray" ff="monospace" tt="none">
              {c.method}
            </Badge>
            <Text fz="sm" ff="monospace" truncate maw={300}>
              {c.path.replace(/^.*\/napi/, "/napi")}
            </Text>
            <RequestIdChip id={c.meta!.requestId} />
          </Group>
          <Group gap="xs" wrap="nowrap">
            <FeedbackButtons requestId={c.meta!.requestId} compact />
            <Button size="compact-xs" variant="subtle" color="gray" leftSection={<IconCode size={13} />} onClick={() => openInspector(c.id)}>
              Trace
            </Button>
          </Group>
        </Group>
      ))}
    </Stack>
  );
}

/** How a Passio call is put together, what comes back, and how to send feedback with the request id. */
export function ApiGuidePage() {
  const { hash } = useLocation();
  const headers = useClientHeaders();
  const prospect = useSession().me?.user.authKind !== "api_key";
  const base = publicApiBase();
  const host = publicApiOrigin().replace(/^https?:\/\//, "");

  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
  }, [hash]);

  const preview = headers.preview();
  const anatomy = `POST /v2/products/napi/tools/vision/extractIngredientsAutoTyped?model=gemini-3-7-flash HTTP/1.1
Host: ${host}
Authorization: Bearer eyJhbGciOi…
Passio-ID: 6f2c…
Localization-ISO: es
Passio-Product-Localization: es-MX
Content-Type: application/json

{"image": "data:image/jpeg;base64,…"}

HTTP/1.1 200 OK
X-Request-ID: 0192f3a4-5b6c-7d8e-9f00-112233445566
X-Model-Tag: gemini-3-7-flash
X-Request-Usage: 2140
X-Billed-Tokens-Total: 2140
Content-Type: application/json

[{"displayName": "Huevos revueltos", "nutritionPreview": {…}}, …]`;

  return (
    <>
      <PageHeader
        eyebrow={`${base.replace(/^https?:\/\//, "")}/napi`}
        title="API guide"
        description="What goes into a call, what comes back, and how to close the loop by rating results with the request id. Everything here works the same from a server, a phone or the SDKs."
      />
      {prospect && (
        <Box mb="lg">
          <AccountCallout placement="guide" title="Make these calls from your own app" body="Create your Passio account to get your API key and pick a plan. The headers and responses on this page are exactly what you'll get." />
        </Box>
      )}
      <Group gap={6} mb="xl" wrap="wrap">
        {SECTIONS.map((s) => (
          <Button key={s.id} component="a" href={`#${s.id}`} size="compact-sm" variant="default" radius="xl">
            {s.label}
          </Button>
        ))}
      </Group>

      <Stack gap={48}>
        <Section id="anatomy" title="Anatomy of a call" lead="Every route is plain HTTPS with JSON in and out. A photo scan, with the parts you control:">
          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 7 }}>
              <CodeBlock code={anatomy} language="http" maxHeight={520} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Timeline active={4} bulletSize={22} lineWidth={2} color="violet">
                <Timeline.Item title="Auth">
                  <Text fz="sm" c="dimmed">
                    <code>Authorization</code> and <code>Passio-ID</code> come from exchanging your license key for a token. Do that on your server and keep the key there; the SDK's <code>LicenseKeyAuth</code> handles it and refreshes the token.
                  </Text>
                </Timeline.Item>
                <Timeline.Item title="Context headers">
                  <Text fz="sm" c="dimmed">
                    Optional: language, region and data-source filters. Set once per app or per user. In this demo they come from Client settings.
                  </Text>
                </Timeline.Item>
                <Timeline.Item title="Model (AI routes)">
                  <Text fz="sm" c="dimmed">
                    Optional <code>?model=</code> to trade cost, speed and accuracy. Leave it out for the route default.
                  </Text>
                </Timeline.Item>
                <Timeline.Item title="Response headers">
                  <Text fz="sm" c="dimmed">
                    A unique <code>X-Request-ID</code>, the model that answered and what the call cost. Store the request id: it's how you rate the result later.
                  </Text>
                </Timeline.Item>
              </Timeline>
            </Grid.Col>
          </Grid>
        </Section>

        <Section id="request-headers" title="Request headers" lead="Auth is required; the rest are optional and apply to every route that can use them.">
          <Card p={0} mb="md">
            <Table verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Header</Table.Th>
                  <Table.Th>What it does</Table.Th>
                  <Table.Th visibleFrom="sm">Example</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {REQUEST_HEADERS.map((h) => (
                  <Table.Tr key={h.name}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text fz="sm" ff="monospace" fw={600}>
                          {h.name}
                        </Text>
                        {h.required && (
                          <Badge size="xs" color="red" variant="light">
                            required
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm">{h.what}</Text>
                    </Table.Td>
                    <Table.Td visibleFrom="sm">
                      <Text fz="xs" ff="monospace" c="dimmed">
                        {h.example}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
          <Card>
            <Group justify="space-between" mb="xs" wrap="wrap">
              <Text fw={700} fz="sm">
                What this browser sends right now
              </Text>
              <Button size="compact-sm" variant="light" leftSection={<IconAdjustmentsHorizontal size={14} />} onClick={openClientSettings}>
                Change in Client settings
              </Button>
            </Group>
            <CodeBlock code={preview.map((h) => `${h.name}: ${h.value}`).join("\n") || "(no optional headers: server defaults)"} language="http" maxHeight={200} />
          </Card>
        </Section>

        <Section
          id="data"
          title="Data sources & filters"
          lead={
            <>
              <code>{REQUEST_HEADER_NAMES.searchConfig}</code> controls which databases a search or AI route draws foods from and which results it keeps. Leave it out to search the Passio database with the defaults.
            </>
          }
        >
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Card>
              <Text fw={700} mb={6}>
                Data sources
              </Text>
              <Stack gap={6}>
                {DATA_SOURCES.map((d) => (
                  <Group key={d.value} justify="space-between" wrap="nowrap">
                    <Text fz="sm">{d.label}</Text>
                    <Text fz="xs" c="dimmed" ff="monospace">
                      {d.default} default · max {d.max}
                    </Text>
                  </Group>
                ))}
              </Stack>
              <Text fz="xs" c="dimmed" mt="sm">
                Each source has a result limit. Add Edamam to widen coverage for ingredients and recipes.
              </Text>
            </Card>
            <Card>
              <Text fw={700} mb={6}>
                Filters
              </Text>
              <Text fz="sm" c="dimmed">
                Keep only some food types ({TYPE_FILTERS.map((t) => t.label.toLowerCase()).join(", ")}), some origins ({SOURCE_FILTERS.slice(0, 3)
                  .map((s) => s.label)
                  .join(", ")}
                …), some regions, or foods with or without certain tags (for example vegan, or no peanuts).
              </Text>
            </Card>
            <Card>
              <Text fw={700} mb={6}>
                Region and language
              </Text>
              <Text fz="sm" c="dimmed">
                <code>{REQUEST_HEADER_NAMES.productLocalization}</code> switches to the regional database and gives AI routes local context: regional dishes, brands and portion names. <code>{REQUEST_HEADER_NAMES.localizationIso}</code> only translates the text you get back.
              </Text>
            </Card>
          </SimpleGrid>
          <Text fz="sm" c="dimmed" mt="sm">
            Build the header in Client settings and the preview above shows the exact JSON to copy into your app. The SDK's <code>PassioSearchConfig</code> builds it in code.
          </Text>
        </Section>

        <Section id="response-headers" title="Response headers" lead="Every response says who answered and what it cost. In this demo, hover the bar at the bottom right after any call, or open the inspector.">
          <Card p={0}>
            <Table verticalSpacing={6} horizontalSpacing="md">
              <Table.Tbody>
                {RESPONSE_HEADER_DOCS.map((h) => (
                  <Table.Tr key={h.name} bg={h.name === "X-Request-ID" ? "var(--mantine-color-violet-0)" : undefined}>
                    <Table.Td w={280}>
                      <Text fz="sm" ff="monospace" fw={h.name === "X-Request-ID" ? 700 : 500}>
                        {h.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm">{h.description}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        </Section>

        <Section
          id="feedback"
          title="Request IDs & feedback"
          lead={
            <>
              Every call returns a unique <code>X-Request-ID</code>. Keep it with the result, and when your user decides what to do with that result, send the outcome to <code>POST /napi/feedback</code>. It isn't billed, there's no deadline, and it's the most useful thing you can do for result quality: Passio sees which answers your users keep, and routes that run on Easy AI learn from it.
            </>
          }
        >
          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Card h="100%">
                <Text fw={700} mb="sm">
                  Example: photo logging
                </Text>
                <Timeline active={5} bulletSize={22} lineWidth={2} color="lime">
                  <Timeline.Item title="Scan">
                    <Text fz="sm" c="dimmed">
                      <code>POST /napi/tools/vision/extractIngredientsAutoTyped</code> returns the foods and <code>X-Request-ID</code>.
                    </Text>
                  </Timeline.Item>
                  <Timeline.Item title="Store the id">
                    <Text fz="sm" c="dimmed">
                      Save it on the draft log entry, next to the foods you show the user.
                    </Text>
                  </Timeline.Item>
                  <Timeline.Item title="User decides">
                    <Text fz="sm" c="dimmed">
                      Logs it as-is → <code>accepted</code>. Changes white rice to brown → <code>edited</code> with <code>itemRef</code> and a <code>note</code>. Deletes the scan → <code>rejected</code>.
                    </Text>
                  </Timeline.Item>
                  <Timeline.Item title="Send it">
                    <Text fz="sm" c="dimmed">
                      One <code>POST /napi/feedback</code>, batching up to 100 items. Add a <code>clientEventId</code> so retries don't double count.
                    </Text>
                  </Timeline.Item>
                  <Timeline.Item title="Look it up">
                    <Text fz="sm" c="dimmed">
                      <code>GET /napi/feedback/&#123;requestId&#125;</code> shows the route, model, tokens and cost of the call, plus the feedback it got. Handy for support tickets too.
                    </Text>
                  </Timeline.Item>
                </Timeline>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Card h="100%">
                <Text fw={700} mb="xs">
                  In code
                </Text>
                <CodeTabs samples={feedbackSamples(base)} />
              </Card>
            </Grid.Col>
          </Grid>

          <Card mt="md" p={0}>
            <Table verticalSpacing="xs" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Result came from</Table.Th>
                  <Table.Th>User did</Table.Th>
                  <Table.Th>Send</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {FEEDBACK_BY_ROUTE.map((r) => (
                  <Table.Tr key={`${r.from}-${r.did}`}>
                    <Table.Td>
                      <Text fz="sm" fw={600}>
                        {r.from}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm">{r.did}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm" ff="monospace">
                        {r.send}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
          <Text fz="xs" c="dimmed" mt={6}>
            Other labels: purchase / conversion, logged, good, saved, copied, neutral, ignored, discarded, bad, reported, or your own (send a <code>score</code> from -1 to 1 with custom labels).
          </Text>

          <Card mt="md">
            <Text fw={700} mb={4}>
              Try it on your recent calls
            </Text>
            <Text fz="sm" c="dimmed" mb="sm">
              These are this session's calls. Rate one, then open its trace to see the feedback recorded against it.
            </Text>
            <RecentRequests />
          </Card>
        </Section>

        <Section id="models" title="Models" lead="AI routes accept ?model=<tag>. Lite models are cheapest and quickest, Pro and Frontier models are the most accurate on hard input.">
          <Group>
            <Button component={Link} to="/models" variant="light" leftSection={<IconCpu size={16} />} rightSection={<IconArrowRight size={14} />}>
              Compare every model
            </Button>
          </Group>
        </Section>

        <Section id="errors" title="Errors & retries" lead="Errors come back as JSON with a message. What to do with each:">
          <Card p={0}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={110}>Status</Table.Th>
                  <Table.Th>Meaning</Table.Th>
                  <Table.Th>What to do</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {ERRORS.map((e) => (
                  <Table.Tr key={e.code}>
                    <Table.Td>
                      <Text fz="sm" ff="monospace" fw={700}>
                        {e.code}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm">{e.meaning}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm" c="dimmed">
                        {e.action}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
          <Text fz="sm" c="dimmed" mt="sm">
            Contacting Passio support about a call? Include its <code>X-Request-ID</code>. The SDK raises <code>PassioApiError</code> with the status and message; see the{" "}
            <Anchor component={Link} to="/developers">
              Client SDKs
            </Anchor>{" "}
            readme.
          </Text>
        </Section>
      </Stack>
    </>
  );
}
